import TableRow from '@mui/material/TableRow'
import { useTranslation } from 'react-i18next'
import { format } from 'date-fns'
import { useMemo } from 'react'
import AdvancedTable from 'components/AdvancedTable'
import { renderCells } from 'utils/renderCells'
import { LabelBox } from 'components/label-box'
import { useAppContext } from 'context/AppContext/AppContext'
import type { BarcodeFailCheck } from './BarcodeFailChecks.types'

const BarcodeFailChecks = (): JSX.Element => {
  const { t } = useTranslation()
  const { markets } = useAppContext()

  const marketOptions = useMemo(
    () =>
      markets !== undefined && 'data' in markets
        ? markets.data
            .sort((a, b) => a.name.localeCompare(b.name))
            .map(el => ({
              id: el.id.toString(),
              value: el.name + (el.description ? ` (${el.description})` : ''),
            }))
        : [],
    [markets]
  )

  return (
    <AdvancedTable<BarcodeFailCheck>
      name="barcode-fail-checks"
      endpoint="barcodes/fail-checks"
      orderField="createdAt"
      orderDir="DESC"
      headings={[
        { type: 'empty', name: 'ID', field: 'id', sort: true, width: 90 },
        { type: 'text', name: t('common.code'), field: 'code', width: 220 },
        { type: 'options', name: t('common.marketName'), field: 'marketId', width: 200 },
        { type: 'datetime-range', name: t('common.createdAt'), field: 'createdAt', sort: true, width: 140 },
      ]}
      options={{
        marketId: marketOptions,
      }}
      renderRow={(row, idx) => (
        <TableRow key={idx}>
          {renderCells([
            { label: 'ID', value: <LabelBox first={`#${row.id}`} /> },
            { label: t('common.code'), value: <LabelBox first={row.code} /> },
            {
              label: t('common.marketName'),
              value: (
                <LabelBox
                  first={marketOptions.find(m => m.id === row.marketId.toString())?.value ?? `#${row.marketId}`}
                />
              ),
            },
            {
              label: t('common.createdAt'),
              value: (
                <LabelBox
                  first={format(new Date(row.createdAt), 'MMM dd, yyyy')}
                  second={format(new Date(row.createdAt), 'HH:mm:ss')}
                />
              ),
            },
          ])}
        </TableRow>
      )}
    />
  )
}

export default BarcodeFailChecks
